import React, { useMemo, useState } from 'react';
import {
    Alert,
    AlertDescription,
    Button,
    ColumnVisibilityMenu,
    DataTable,
    ToastProvider,
    useDataTable,
    useToast,
} from '@onward-delivery/design';
import { Plus as PlusIcon } from '@onward-delivery/design/icons';

import withOnwardAdminRights from '@/components/Auth/withOnwardAdminRights';

import { automationColumns } from './columns';
import { useAutomations } from './hooks';
import { withClientNames } from './utils';
import AutomationSheet from './AutomationSheet';
import DeleteAutomationDialog from './DeleteAutomationDialog';

const AutomationsPage = () => {
    const { toast } = useToast();
    const { automations, loading, error, clients, saving, deleting, save, remove } = useAutomations();

    // undefined = closed, null = creating, row = editing
    const [editing, setEditing] = useState(undefined);
    const [deletingRow, setDeletingRow] = useState(null);
    const [columnVisibility, setColumnVisibility] = useState({ middle_mile_carrier_id: false });

    const columns = useMemo(() => automationColumns({ onEdit: setEditing, onDelete: setDeletingRow }), []);
    const rows = useMemo(() => withClientNames(automations, clients.names), [automations, clients.names]);

    const table = useDataTable({
        columns,
        data: rows,
        getRowId: (row) => row.automation_id,
        columnPinning: { right: ['actions'] },
        columnVisibility,
        onColumnVisibilityChange: setColumnVisibility,
    });

    const urlSuggestions = useMemo(() => [...new Set(automations.map((row) => row.url))].sort(), [automations]);

    const handleSave = async (automationId, input) => {
        await save(automationId, input);
        toast({ title: automationId ? 'Automation updated' : 'Automation created' });
    };

    const handleDelete = async (automation) => {
        try {
            await remove(automation.automation_id);
            toast({ title: 'Automation deleted' });
            setDeletingRow(null);
        } catch (e) {
            toast({ title: 'Could not delete automation', description: e.message, variant: 'destructive' });
        }
    };

    return (
        <div className="flex h-full min-h-0 w-full min-w-0 flex-col gap-4 p-10">
            <div className="flex items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-semibold text-foreground">Automations</h1>
                    <p className="text-sm text-muted">Scheduled jobs that call Onward functions on a crontab.</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    <ColumnVisibilityMenu table={table} />
                    <Button onClick={() => setEditing(null)}>
                        <PlusIcon className="mr-2 h-4 w-4" />
                        New automation
                    </Button>
                </div>
            </div>

            {error && (
                <Alert variant="destructive">
                    <AlertDescription>Automations could not be loaded: {error.message}</AlertDescription>
                </Alert>
            )}

            <DataTable
                table={table}
                isLoading={loading}
                emptyState="No automations yet"
                className="mb-24 flex min-h-0 w-full min-w-0 flex-1 flex-col"
            />

            <AutomationSheet
                open={editing !== undefined}
                automation={editing}
                onOpenChange={(open) => !open && setEditing(undefined)}
                onSave={handleSave}
                saving={saving}
                clients={clients}
                urlSuggestions={urlSuggestions}
            />
            <DeleteAutomationDialog
                automation={deletingRow}
                onOpenChange={(open) => !open && setDeletingRow(null)}
                onConfirm={handleDelete}
                deleting={deleting}
            />
        </div>
    );
};

const AdminAutomations = () => (
    <div className="onward-design">
        <ToastProvider>
            <AutomationsPage />
        </ToastProvider>
    </div>
);

export default withOnwardAdminRights(AdminAutomations);
